import axios from 'axios';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : 'http://localhost:8080/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let authToken = null;

const apiService = {
  setAuthToken: (token) => {
    authToken = token;
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  },

  login: (username, password) => {
    return api.post('/auth/login', {
      username,
      password,
      applicationType: 'WEB'
    });
  },

  register: (username, password) => {
    return api.post('/auth/register', { username, password });
  },

  getCars: (page = 0, size = 20) => {
    return api.get('/cars', { params: { page, size } });
  },

  getRecentCars: (hours = 24) => {
    return api.get(`/cars/recent?hours=${hours}`);
  },

  getUserFilteredCars: (hours = 24) => {
    return api.get('/cars/user-filtered', { params: { hours } });
  },

  getDamagedCars: (hours = 168) => {
    return api.get('/cars/damaged', { params: { hours } });
  },

  getFirstOwnerCars: (hours = 168) => {
    return api.get('/cars/first-owner', { params: { hours } });
  },

  getGebrauchtwagenCars: (hours = 168) => {
    return api.get('/cars/gebrauchtwagen', { params: { hours } });
  },

  getCar: (id) => {
    return api.get(`/cars/${id}`);
  },

  getCarStats: () => {
    return api.get('/cars/stats');
  },

  getUserFilter: () => {
    return api.get('/user-filter');
  },

  updateUserFilter: (filter) => {
    return api.put('/user-filter', filter);
  },

  // SMS console (dealership)
  getSmsProfile: () => {
    return api.get('/sms/profile');
  },

  updateSmsProfile: (profile) => {
    return api.put('/sms/profile', profile);
  },

  sendSms: (payload) => {
    return api.post('/sms/send', payload);
  },

  getGebrauchtwagenFilter: () => {
    return api.get('/cars/gebrauchtwagen/filter');
  },

  saveGebrauchtwagenFilter: (filter) => {
    return api.put('/cars/gebrauchtwagen/filter', filter);
  },

  getSmsUsage: () => {
    return api.get('/sms/usage');
  },

  getSmsSentRecipients: () => {
    return api.get('/sms/recipients-sent');
  },

  sendSmsBulk: (payload) => {
    return api.post('/sms/send-bulk', payload);
  },

  // Admin (package management)
  getAdminUsers: () => {
    return api.get('/admin/users');
  },

  setUserPackage: (id, smsPackage) => {
    return api.put(`/admin/users/${id}/package`, { smsPackage });
  },

  setUserType: (id, userType) => {
    return api.put(`/admin/users/${id}/type`, { userType });
  },

  setUserGebrauchtwagen: (id, enabled) => {
    return api.put(`/admin/users/${id}/gebrauchtwagen`, { enabled });
  },

  createUser: (payload) => {
    return api.post('/admin/users', payload);
  },

  deleteUser: (id) => {
    return api.delete(`/admin/users/${id}`);
  },

  setUserActive: (id, active) => {
    return api.put(`/admin/users/${id}/active`, { active });
  },

  resetUserPassword: (id, password) => {
    return api.put(`/admin/users/${id}/password`, { password });
  },

  getSmsHistory: () => {
    return api.get('/sms/history');
  },

  // Admin (dashboard + per-dealership send evidence)
  getDashboard: () => {
    return api.get('/admin/dashboard');
  },

  getDealershipSms: (username) => {
    return api.get(`/admin/sms/${encodeURIComponent(username)}`);
  },

  getUserLogins: (id) => {
    return api.get(`/admin/users/${id}/logins`);
  },

  // Admin (proxy management)
  getProxies: () => {
    return api.get('/admin/proxies');
  },

  addProxy: (host, port, username, password) => {
    return api.post('/admin/proxies', { host, port, username, password });
  },

  deleteProxy: (id) => {
    return api.delete(`/admin/proxies/${id}`);
  },

  toggleProxy: (id) => {
    return api.put(`/admin/proxies/${id}/toggle`);
  },

  setProxyService: (enabled) => {
    return api.put('/admin/proxy-service', { enabled });
  },

  // Admin (dealerships / avtohiše)
  getDealers: (page = 0, size = 50, search = '') => {
    return api.get('/admin/dealers', { params: { page, size, search } });
  },

  getDealer: (id) => {
    return api.get(`/admin/dealers/${id}`);
  },

  deleteDealer: (id) => {
    return api.delete(`/admin/dealers/${id}`);
  },

  updateDealerNotes: (id, notes) => {
    return api.put(`/admin/dealers/${id}/notes`, { notes });
  },

  cleanupEmptyDealers: () => {
    return api.post('/admin/dealers/cleanup-empty');
  },

  getDealerStatus: () => {
    return api.get('/admin/dealers/status');
  },

  scanDealers: () => {
    return api.post('/admin/dealers/scan');
  },

  // Dealer scraper proxies (source + dedicated pool)
  getDealerProxies: () => {
    return api.get('/admin/dealers/proxies');
  },

  setDealerProxySource: (source) => {
    return api.put('/admin/dealers/proxy-source', { source });
  },

  addDealerProxy: (host, port, username, password) => {
    return api.post('/admin/dealers/proxies', { host, port, username, password });
  },

  deleteDealerProxy: (id) => {
    return api.delete(`/admin/dealers/proxies/${id}`);
  },

  toggleDealerProxy: (id) => {
    return api.put(`/admin/dealers/proxies/${id}/toggle`);
  },

  // Web Push (PWA notifications)
  getWebPushPublicKey: () => {
    return api.get('/web-push/public-key');
  },

  subscribeWebPush: (subscription) => {
    return api.post('/web-push/subscribe', subscription);
  },

  unsubscribeWebPush: (payload) => {
    return api.post('/web-push/unsubscribe', payload);
  },

  testWebPush: () => {
    return api.post('/web-push/test');
  },
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // The API returns 403 (not only 401) for a missing/invalid/expired token.
    // Treat both as "not authenticated" so a stale token doesn't trap the user.
    const status = error.response?.status;
    if ((status === 401 || status === 403) && localStorage.getItem('token')) {
      // Clear auth data and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default apiService;